import { useCallback, useEffect, useRef, useState } from 'react';

const RECONNECTION_TIMEOUT_MS = 30_000; // 30 seconds

/**
 * Reconnection timeout hook.
 *
 * When buffering is detected, starts a 30-second countdown.
 * If playback doesn't resume within 30 seconds, signals a timeout error.
 *
 * Requirements:
 * - Display buffering indicator on network interruption (Req 1.6)
 * - Attempt reconnection for up to 30 seconds (Req 1.6)
 * - Show error message after timeout (Req 1.6)
 */
export function useReconnection(isBuffering: boolean) {
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(30);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    setIsTimedOut(false);
    setRemainingSeconds(30);
  }, [clearTimers]);

  useEffect(() => {
    if (isBuffering && !isTimedOut) {
      // Start countdown
      const startTime = Date.now();

      countdownRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(
          0,
          Math.ceil((RECONNECTION_TIMEOUT_MS - elapsed) / 1000)
        );
        setRemainingSeconds(remaining);
      }, 1000);

      timeoutRef.current = setTimeout(() => {
        setIsTimedOut(true);
        clearTimers();
      }, RECONNECTION_TIMEOUT_MS);
    } else if (!isBuffering) {
      // Playback resumed — reset
      reset();
    }

    return clearTimers;
  }, [isBuffering, isTimedOut, clearTimers, reset]);

  return { isTimedOut, remainingSeconds, reset };
}
